import { useCallback, useState } from 'react';
import { t } from '@bms2/shared';
import { devicePreferenceStore, readPreference, writePreference } from '../../shell/devicePreference';
import { Icon } from '../Icon';
import styles from './FoldChevron.module.css';

/**
 * Per-device fold memory for a set of named groups (decision 6211, extending 0500): a grouped list's band and a
 * Discovery card's group or nested block fold to their heading, and the choice is read from and written to the one
 * guarded device-preference module under `prefix` plus the group's own id. A browser that refuses to store reads as
 * open, which is the safe way to be wrong.
 */
export function useFoldMemory(prefix: string): { isFolded: (id: string) => boolean; toggle: (id: string) => void } {
  const [, redraw] = useState(0);
  const isFolded = useCallback((id: string) => readPreference(`${prefix}${id}`, (raw) => raw === 'folded', false, devicePreferenceStore()), [prefix]);
  const toggle = useCallback(
    (id: string) => {
      writePreference(`${prefix}${id}`, isFolded(id) ? 'open' : 'folded');
      redraw((n) => n + 1);
    },
    [prefix, isFolded],
  );
  return { isFolded, toggle };
}

/** The chevron a folding heading carries; the heading and its count stay standing either way. */
export function FoldChevron({ folded, onToggle, name }: { folded: boolean; onToggle: () => void; name: string }) {
  const label = `${t(folded ? 'group.expand' : 'group.collapse')}: ${name}`;
  return (
    <button type="button" className={styles.fold} onClick={onToggle} aria-expanded={!folded} aria-label={label} title={label} data-group-fold={folded ? 'folded' : 'open'}>
      <Icon meaning={folded ? 'expand' : 'collapse'} size="inline" />
    </button>
  );
}
